import { useCallback, useEffect, useMemo, useState } from 'react';
import { getManagerMovements } from '@/api/manager-balance/managerBalanceApi';
import { MANAGER_MOVEMENTS_FIELDS } from '@/api/manager-balance/manager-movements-fields.config';
import { getMyDebtPayments } from '@/api/manager-balance/debtPaymentsApi';
import { handleGlobalError } from '@/utils/error-handler';
import { buildUnifiedMovements } from '@/utils/movements-utils';
import { useAuthStore } from '@/store/authStore';
import type {
  DebtPaymentRequest,
  ManagerMovement,
  UnifiedMovement,
} from '@/types/manager.model';

interface CurrentAccountState {
  negativeBalance: number;
  maxBalance: number;
  movements: UnifiedMovement[];
  debtPayments: DebtPaymentRequest[];
  pendingRequests: DebtPaymentRequest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Loads the full Current Account state: balance + unified movement history
 * (tournaments/classes via `/payments/me/movements` and transfers via
 * `/payments/debt-payments/me`, merged on the client).
 */
export const useCurrentAccount = (): CurrentAccountState => {
  const [rawMovements, setRawMovements] = useState<ManagerMovement[]>([]);
  const [debtPayments, setDebtPayments] = useState<DebtPaymentRequest[]>([]);
  const [negativeBalance, setNegativeBalance] = useState(0);
  const [maxBalance, setMaxBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setError(null);
    try {
      const [movementsResponse, debtResponse] = await Promise.all([
        getManagerMovements(MANAGER_MOVEMENTS_FIELDS.MINIMAL),
        getMyDebtPayments(),
      ]);
      setRawMovements(movementsResponse.movements ?? []);
      setNegativeBalance(movementsResponse.negativeBalance ?? 0);
      setMaxBalance(movementsResponse.maxBalance ?? 0);
      setDebtPayments(debtResponse);
    } catch (err) {
      console.error('Error fetching current account:', err);
      setError('No se pudo cargar la información de la cuenta corriente.');
      handleGlobalError(
        err,
        'Error',
        'No se pudo cargar la información de la cuenta corriente.',
      );
    }
  }, []);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    fetchAll().finally(() => {
      if (active) setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [fetchAll]);

  const sessionEmail = useAuthStore((state) => state.user?.email ?? null);

  const movements = useMemo(
    () => buildUnifiedMovements(rawMovements, debtPayments, { sessionEmail }),
    [rawMovements, debtPayments, sessionEmail],
  );

  const pendingRequests = useMemo(
    () => debtPayments.filter((req) => req.status === 'PENDING_REVIEW'),
    [debtPayments],
  );

  return {
    negativeBalance,
    maxBalance,
    movements,
    debtPayments,
    pendingRequests,
    isLoading,
    error,
    refetch: fetchAll,
  };
};
